/**
 * EncycGames subscription catalog — one source of truth for in-app plans.
 *
 * Public marketing stays high-level (Players/Parents · Academies · Schools · Corporate).
 * Player/parent dashboards use two categories (Individual | Family), each with
 * Single, Multi-sport, and Elite. Family includes 2 adults + 1 child; extra children
 * are add-ons only. Sport prices, plan premiums, and add-on amounts live here so SKUs
 * can change later.
 *
 * Yearly prices come from the selected country's plan rates (admin-editable).
 * Sports share one plan price — Single / Multi-sport / Elite covers capacity.
 */

import type {
  BetaAccessLevel,
  FamilyPlanConfig,
  PlayerPlanId,
  SubscriptionCategory,
  SubscriptionIntent,
  User,
} from '@types/index';
import {
  DEFAULT_SUBSCRIPTION_OFFERS,
  earlyBirdPrice,
  getEffectiveYearlyPrice,
  suggestedOfferIdForRole,
  type SubscriptionOffer,
  type SubscriptionTierId,
} from '@/content/subscriptionPricing';

export type SubscriptionAudience =
  | 'public'
  | 'player_parent'
  | 'coach'
  | 'academy'
  | 'school'
  | 'corporate';

export type {
  BetaAccessLevel,
  FamilyPlanConfig,
  PlayerPlanId,
  SubscriptionCategory,
  SubscriptionIntent,
};

export const PLAYER_PLAN_IDS: PlayerPlanId[] = ['basic', 'multi', 'elite'];
export const SUBSCRIPTION_CATEGORIES: SubscriptionCategory[] = ['individual', 'family'];

export interface CatalogSport {
  id: string;
  name: string;
  /** Unused for quoting — plan country rates set the price. */
  yearlyUsd?: number;
}

export interface PlanEnabledByCategory {
  individual: boolean;
  family: boolean;
}

export interface PlayerPlanOffering {
  id: PlayerPlanId;
  name: string;
  tagline: string;
  includedSports: number;
  includedChildren: number;
  premiumUsd: number;
  includesPublicProfile: boolean;
  highlight?: boolean;
  /**
   * Purchasable when true. Disabled plans stay visible but greyed — no price,
   * Purchase off. Pre-book still works. Defaults: Elite off, Single/Multi on.
   */
  enabled?: boolean;
  /** Per-category override (Individual / Family). Falls back to `enabled`. */
  enabledByCategory?: PlanEnabledByCategory;
  /** Internal only — never shown on player/coach/public subscription UI. */
  betaAccess?: BetaAccessLevel;
  /** Internal ledger flag. Do not surface the word Beta to users. */
  beta?: boolean;
  /** Plan is reserved rather than purchased when selected. */
  prebook?: boolean;
  features: string[];
}

export interface FeatureAddOn {
  id: string;
  name: string;
  description: string;
  yearlyUsd: number;
  includedInPlans: PlayerPlanId[];
}

export interface FamilyHousehold {
  includedAdults: number;
  includedChildren: number;
  yearlyUsd: number;
  extraChildUsd: number;
  description: string;
}

export interface PlanPriceMap {
  basic: number;
  multi: number;
  elite: number;
}

export interface SubscriptionCountryRate {
  code: string;
  name: string;
  currency: string;
  individual: PlanPriceMap;
  family: PlanPriceMap;
  extraSport: number;
  extraChild: number;
}

export interface PlayerFamilyCatalog {
  billingPeriod: 'yearly';
  currency: string;
  baseOfferId: 'players_parents';
  includedSports: number;
  includedChildren: number;
  extraSportUsd: number;
  extraChildUsd: number;
  publicProfileUsd: number;
  maxSports: number;
  maxChildren: number;
  sports: CatalogSport[];
  plans: PlayerPlanOffering[];
  featureAddOns: FeatureAddOn[];
  familyHousehold: FamilyHousehold;
  /** Admin-configurable yearly discount when switching Individual → Family. */
  individualToFamilyDiscountPercent: number;
  headline: string;
  termsNote: string;
  countryCode?: string;
  countryName?: string;
  defaultCountryCode?: string;
  planPrices?: {
    individual: PlanPriceMap;
    family: PlanPriceMap;
  };
  countryRates?: SubscriptionCountryRate[];
}

const DEFAULT_SPORTS: CatalogSport[] = [
  { id: 'football', name: 'Football' },
  { id: 'cricket', name: 'Cricket' },
  { id: 'badminton', name: 'Badminton' },
  { id: 'tennis', name: 'Tennis' },
];

export const DEFAULT_COUNTRY_RATES: SubscriptionCountryRate[] = [
  {
    code: 'US',
    name: 'United States',
    currency: 'USD',
    individual: { basic: 80, multi: 115, elite: 165 },
    family: { basic: 170, multi: 205, elite: 255 },
    extraSport: 40,
    extraChild: 50,
  },
  {
    code: 'IN',
    name: 'India',
    currency: 'INR',
    individual: { basic: 6600, multi: 9500, elite: 13700 },
    family: { basic: 14100, multi: 17000, elite: 21100 },
    extraSport: 3300,
    extraChild: 4100,
  },
  {
    code: 'GB',
    name: 'United Kingdom',
    currency: 'GBP',
    individual: { basic: 65, multi: 90, elite: 130 },
    family: { basic: 135, multi: 160, elite: 200 },
    extraSport: 30,
    extraChild: 40,
  },
  {
    code: 'AE',
    name: 'United Arab Emirates',
    currency: 'AED',
    individual: { basic: 295, multi: 420, elite: 605 },
    family: { basic: 625, multi: 750, elite: 935 },
    extraSport: 145,
    extraChild: 185,
  },
  {
    code: 'SG',
    name: 'Singapore',
    currency: 'SGD',
    individual: { basic: 110, multi: 155, elite: 220 },
    family: { basic: 230, multi: 275, elite: 340 },
    extraSport: 55,
    extraChild: 70,
  },
];

export const DEFAULT_SUBSCRIPTION_COUNTRY_CODE = 'US';

export function defaultCountryRate(
  rates: SubscriptionCountryRate[] = DEFAULT_COUNTRY_RATES,
  defaultCode = DEFAULT_SUBSCRIPTION_COUNTRY_CODE,
): SubscriptionCountryRate {
  return rates.find((row) => row.code === defaultCode) || rates[0] || DEFAULT_COUNTRY_RATES[0];
}

export const PLAYER_FAMILY_CATALOG: PlayerFamilyCatalog = {
  billingPeriod: 'yearly',
  currency: 'USD',
  baseOfferId: 'players_parents',
  includedSports: 1,
  includedChildren: 1,
  extraSportUsd: 40,
  extraChildUsd: 50,
  publicProfileUsd: 25,
  maxSports: 5,
  maxChildren: 8,
  sports: DEFAULT_SPORTS,
  plans: [
    {
      id: 'basic',
      name: 'Single',
      tagline: '1 sport · start training',
      includedSports: 1,
      includedChildren: 1,
      premiumUsd: 0,
      includesPublicProfile: true,
      enabled: true,
      enabledByCategory: { individual: true, family: true },
      betaAccess: 'none',
      features: [
        '1 sport of your choice',
        '1 player seat',
        'Public player profile page',
        'Technical Foundation contests',
        'Self-evaluation',
        'Age-wise learning library',
        '2 assessments per year',
        'Progress tracking',
        'Standard email support',
      ],
    },
    {
      id: 'multi',
      name: 'Multi-sport',
      tagline: 'Up to 3 sports',
      includedSports: 3,
      includedChildren: 1,
      premiumUsd: 35,
      includesPublicProfile: true,
      enabled: true,
      enabledByCategory: { individual: true, family: true },
      betaAccess: 'limited',
      features: [
        'Up to 3 sports',
        '1 player seat',
        'Public player profile page',
        'Technical Foundation contests across selected sports',
        'Self-evaluation and coach assessment',
        'Age-wise learning library',
        '4 assessments per year — unused carry forward',
        'Certificates and progress tracking',
        'Leaderboard visibility',
        'Standard email support',
      ],
    },
    {
      id: 'elite',
      name: 'Elite',
      tagline: 'Up to 3 sports · most features',
      includedSports: 3,
      includedChildren: 1,
      premiumUsd: 85,
      includesPublicProfile: true,
      highlight: true,
      enabled: false,
      enabledByCategory: { individual: false, family: false },
      betaAccess: 'full',
      features: [
        'Up to 3 sports',
        '1 player seat',
        'Everything in Multi-sport',
        'Public player profile page',
        '6 assessments per year — unused carry forward',
        'Certificates and shareable badges',
        'Featured leaderboard placement',
        'Priority support',
        'Early contest access',
      ],
    },
  ],
  featureAddOns: [],
  familyHousehold: {
    includedAdults: 2,
    includedChildren: 1,
    yearlyUsd: 0,
    extraChildUsd: 50,
    description: '2 adults + 1 child included. Additional children are add-ons.',
  },
  individualToFamilyDiscountPercent: 15,
  headline: 'Choose a yearly plan',
  termsNote:
    '*Placeholder offerings — plan features and country rates can change. Terms & conditions apply.',
  countryCode: DEFAULT_COUNTRY_RATES[0].code,
  countryName: DEFAULT_COUNTRY_RATES[0].name,
  defaultCountryCode: DEFAULT_SUBSCRIPTION_COUNTRY_CODE,
  planPrices: {
    individual: { ...DEFAULT_COUNTRY_RATES[0].individual },
    family: { ...DEFAULT_COUNTRY_RATES[0].family },
  },
  countryRates: DEFAULT_COUNTRY_RATES,
};

export function catalogSportNames(catalog: PlayerFamilyCatalog = PLAYER_FAMILY_CATALOG): string[] {
  return catalog.sports.map((sport) => sport.name);
}

export function normalizeSportId(value?: string | null): string {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_|_$/g, '');
}

export function findCatalogSport(
  value?: string | null,
  catalog: PlayerFamilyCatalog = PLAYER_FAMILY_CATALOG,
): CatalogSport | undefined {
  const id = normalizeSportId(value);
  if (!id) return undefined;
  return catalog.sports.find(
    (sport) => normalizeSportId(sport.id) === id || normalizeSportId(sport.name) === id,
  );
}

export function sportYearlyUsd(
  _value?: string | null,
  catalog: PlayerFamilyCatalog = PLAYER_FAMILY_CATALOG,
): number {
  return catalog.extraSportUsd;
}

export function planYearlyAmount(
  plan: PlayerPlanOffering | PlayerPlanId | string | null | undefined,
  catalog: PlayerFamilyCatalog = PLAYER_FAMILY_CATALOG,
  category: SubscriptionCategory = 'individual',
): number {
  const resolved = typeof plan === 'object' && plan ? plan : findPlayerPlan(plan, catalog);
  const prices = catalog.planPrices?.[category];
  const fromRate = prices?.[resolved.id];
  if (Number.isFinite(Number(fromRate))) return Number(fromRate);
  return Number(resolved.premiumUsd) || 0;
}

export function findPlayerPlan(
  planId?: string | null,
  catalog: PlayerFamilyCatalog = PLAYER_FAMILY_CATALOG,
): PlayerPlanOffering {
  return catalog.plans.find((plan) => plan.id === planId) || catalog.plans[0];
}

export function defaultPlanEnabled(planId?: string | null): boolean {
  return String(planId || '') !== 'elite';
}

export function defaultPlanEnabledByCategory(planId?: string | null): PlanEnabledByCategory {
  const on = defaultPlanEnabled(planId);
  return { individual: on, family: on };
}

export function resolvePlanEnabledByCategory(
  plan?: Pick<PlayerPlanOffering, 'id' | 'enabled' | 'enabledByCategory'> | null,
): PlanEnabledByCategory {
  const fallback = defaultPlanEnabledByCategory(plan?.id);
  const byCategory = plan?.enabledByCategory;
  if (byCategory && typeof byCategory === 'object') {
    return {
      individual:
        typeof byCategory.individual === 'boolean' ? byCategory.individual : fallback.individual,
      family: typeof byCategory.family === 'boolean' ? byCategory.family : fallback.family,
    };
  }
  if (typeof plan?.enabled === 'boolean') {
    return { individual: plan.enabled, family: plan.enabled };
  }
  return fallback;
}

export function isPlanEnabled(
  plan?: Pick<PlayerPlanOffering, 'id' | 'enabled' | 'enabledByCategory'> | null,
  category: SubscriptionCategory = 'individual',
): boolean {
  const resolved = resolvePlanEnabledByCategory(plan);
  return Boolean(resolved[category]);
}

export function isCatalogPlanEnabled(
  catalog: PlayerFamilyCatalog,
  planId: PlayerPlanId | string | null | undefined,
  category: SubscriptionCategory = 'individual',
): boolean {
  return isPlanEnabled(findPlayerPlan(planId, catalog), category);
}

const BETA_COPY = /\bbeta\b/i;

/** User-facing feature lines — drop leftover Beta copy. */
export function publicPlanFeatures(features?: string[] | null): string[] {
  return (features || [])
    .map((line) => {
      const text = String(line || '').trim();
      if (/full beta features and early contest access/i.test(text)) return 'Early contest access';
      return text;
    })
    .filter((line) => line && !BETA_COPY.test(line));
}

export const DISABLED_PLAN_LAUNCH_NOTE =
  'Not available for purchase yet. Pre-book now — your reservation upgrades onto this plan when it launches.';

export const OCCUPIED_SUBSCRIPTION_STATUSES = ['active', 'prebooked', 'beta'] as const;

export function isOccupiedSubscription(status?: string | null): boolean {
  return OCCUPIED_SUBSCRIPTION_STATUSES.includes(
    String(status || '') as (typeof OCCUPIED_SUBSCRIPTION_STATUSES)[number],
  );
}

export function subscriptionStatusLabel(status?: string | null): string {
  switch (String(status || '')) {
    case 'active':
      return 'Active';
    case 'expired':
      return 'Expired';
    case 'pending':
    case 'pending_payment':
      return 'Pending payment';
    case 'prebooked':
      return 'Pre-booked';
    case 'beta':
      return 'Active';
    case 'cancelled':
      return 'Cancelled';
    default:
      return 'No plan yet';
  }
}

export function invoiceStatusLabel(status?: string | null): string {
  switch (String(status || '')) {
    case 'active':
      return 'Paid';
    case 'prebooked':
      return 'Reserved';
    case 'beta':
      return 'Paid';
    case 'expired':
      return 'Expired';
    case 'cancelled':
      return 'Cancelled';
    case 'pending_payment':
    case 'pending':
      return 'Open';
    default:
      return status ? String(status) : 'Recorded';
  }
}

/** Stripe-style lowercase invoice status for the billing table. */
export function invoiceStripeStatus(status?: string | null, kind?: string | null): string {
  if (kind === 'reservation' || status === 'prebooked') return 'open';
  if (status === 'pending_payment' || status === 'pending') return 'open';
  if (status === 'paid' || status === 'open' || status === 'void' || status === 'draft') return String(status);
  return 'paid';
}

export interface PlanOption {
  category: SubscriptionCategory;
  planId: PlayerPlanId;
  name: string;
  rank: number;
}

export function planLadderRank(category?: string | null, planId?: string | null): number {
  const planIndex = PLAYER_PLAN_IDS.indexOf((planId || 'basic') as PlayerPlanId);
  const planRank = planIndex >= 0 ? planIndex : 0;
  return (category === 'family' ? 10 : 0) + planRank;
}

export function listAllPlanOptions(catalog: PlayerFamilyCatalog = PLAYER_FAMILY_CATALOG): PlanOption[] {
  const options: PlanOption[] = [];
  SUBSCRIPTION_CATEGORIES.forEach((category) => {
    catalog.plans.forEach((plan) => {
      options.push({
        category,
        planId: plan.id,
        name: `${category === 'family' ? 'Family' : 'Individual'} ${plan.name}`,
        rank: planLadderRank(category, plan.id),
      });
    });
  });
  return options;
}

export function planChangeKind(
  from: { category?: string | null; planId?: string | null } | null | undefined,
  to: { category: SubscriptionCategory; planId: PlayerPlanId },
): 'current' | 'upgrade' | 'downgrade' {
  const fromRank = planLadderRank(from?.category, from?.planId);
  const toRank = planLadderRank(to.category, to.planId);
  if (toRank === fromRank) return 'current';
  return toRank > fromRank ? 'upgrade' : 'downgrade';
}

export function planDisplayName(configInput?: Partial<FamilyPlanConfig> | null): string {
  const config = clampFamilyPlanConfig(configInput);
  const plan = findPlayerPlan(config.planId);
  return `${config.category === 'family' ? 'Family' : 'Individual'} ${plan.name}`;
}

export function applyIndividualToFamilyDiscount(
  payableUsd: number,
  catalog: PlayerFamilyCatalog = PLAYER_FAMILY_CATALOG,
  fromCategory?: SubscriptionCategory | null,
  toCategory?: SubscriptionCategory | null,
): { payableUsd: number; individualToFamilyDiscountPercent: number; individualToFamilyDiscountUsd: number } {
  if (fromCategory !== 'individual' || toCategory !== 'family') {
    return { payableUsd, individualToFamilyDiscountPercent: 0, individualToFamilyDiscountUsd: 0 };
  }
  const pct = Math.min(100, Math.max(0, Number(catalog.individualToFamilyDiscountPercent) || 0));
  const discountUsd = Math.round(payableUsd * (pct / 100) * 100) / 100;
  return {
    payableUsd: Math.round((payableUsd - discountUsd) * 100) / 100,
    individualToFamilyDiscountPercent: pct,
    individualToFamilyDiscountUsd: discountUsd,
  };
}

export function planBetaAccess(plan?: PlayerPlanOffering | null): BetaAccessLevel {
  return plan?.betaAccess || 'none';
}

export function selectionRequiresPrebook(
  input?: Partial<FamilyPlanConfig> | null,
  catalog: PlayerFamilyCatalog = PLAYER_FAMILY_CATALOG,
): boolean {
  return Boolean(findPlayerPlan(input?.planId, catalog).prebook);
}

export function selectionIncludesBeta(
  input?: Partial<FamilyPlanConfig> | null,
  catalog: PlayerFamilyCatalog = PLAYER_FAMILY_CATALOG,
): boolean {
  const plan = findPlayerPlan(input?.planId, catalog);
  return Boolean(plan.beta || planBetaAccess(plan) !== 'none');
}

export function resolveCheckoutIntent(
  input?: Partial<FamilyPlanConfig> | null,
  catalog: PlayerFamilyCatalog = PLAYER_FAMILY_CATALOG,
): SubscriptionIntent {
  return selectionRequiresPrebook(input, catalog) ? 'prebook' : 'purchase';
}

function uniqueStrings(values: Array<string | null | undefined>): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  values.forEach((value) => {
    const next = String(value || '').trim();
    if (!next || seen.has(next)) return;
    seen.add(next);
    out.push(next);
  });
  return out;
}

export function resolveSportIds(
  input?: Partial<FamilyPlanConfig> | null,
  catalog: PlayerFamilyCatalog = PLAYER_FAMILY_CATALOG,
): string[] {
  const fromIds = Array.isArray(input?.sportIds) ? input.sportIds : [];
  const resolved = uniqueStrings(
    fromIds.map((id) => findCatalogSport(id, catalog)?.id).filter(Boolean) as string[],
  );
  if (resolved.length) return resolved.slice(0, catalog.maxSports);

  const count = Math.min(
    catalog.maxSports,
    Math.max(0, Number(input?.sportsCount) || 0),
  );
  return catalog.sports.slice(0, count).map((sport) => sport.id);
}

function inferPlanId(
  input?: Partial<FamilyPlanConfig> | null,
  catalog: PlayerFamilyCatalog = PLAYER_FAMILY_CATALOG,
): PlayerPlanId {
  if (input?.planId && PLAYER_PLAN_IDS.includes(input.planId)) return input.planId;
  const sportsCount = resolveSportIds(input, catalog).length || Number(input?.sportsCount) || 1;
  return sportsCount > 1 ? 'multi' : 'basic';
}

export function resolveSubscriptionCategory(
  input?: Partial<FamilyPlanConfig> | null,
): SubscriptionCategory {
  if (input?.category === 'family' || input?.category === 'individual') return input.category;
  if (input?.familyPackage) return 'family';
  return 'individual';
}

export function familyIncludedSeats(catalog: PlayerFamilyCatalog = PLAYER_FAMILY_CATALOG): string {
  const { includedAdults, includedChildren } = catalog.familyHousehold;
  return `${includedAdults} adult${includedAdults === 1 ? '' : 's'} + ${includedChildren} child`;
}

export function planFeaturesForCategory(
  plan: PlayerPlanOffering,
  category: SubscriptionCategory,
  catalog: PlayerFamilyCatalog = PLAYER_FAMILY_CATALOG,
): string[] {
  const seatLine = category === 'family' ? `${familyIncludedSeats(catalog)} included` : '1 player seat';
  return publicPlanFeatures(plan.features).map((feature) =>
    feature === '1 player seat' || feature.includes('adults +') ? seatLine : feature,
  );
}

/** Deferred student-management SKU. 1.0 coaches buy Individual | Family player plans. */
export const COACH_IN_APP_OFFER: SubscriptionOffer = {
  id: 'coaches' as SubscriptionTierId,
  name: 'Coach',
  audience: 'Independent and academy coaches',
  billingPeriod: 'yearly',
  currency: 'USD',
  yearlyUsd: null,
  earlyBirdPercent: 0,
  earlyBirdEnabled: false,
  customPricing: true,
  sports: catalogSportNames(),
  features: [
    'Coach workspace for assigned players',
    'Assessment and contest workflows',
    'Learning library access',
    'Quoted yearly by roster size',
  ],
  includesNote: 'Custom yearly · contact sales',
  offerHeadline: 'Coach plans are quoted for your roster',
  termsNote: '*Terms & conditions apply. Coach packages are tailored after a short discovery call.',
  ctaLabel: 'Contact sales',
  ctaPath: '/subscriptions/contact?plan=coaches',
};

export function defaultFamilyPlanConfig(
  catalog: PlayerFamilyCatalog = PLAYER_FAMILY_CATALOG,
): FamilyPlanConfig {
  return {
    planId: 'basic',
    category: 'individual',
    sportIds: [],
    sportsCount: 0,
    childrenCount: catalog.includedChildren,
    extraChildren: 0,
    publicProfile: true,
    featureAddOnIds: [],
    familyPackage: false,
  };
}

export function clampFamilyPlanConfig(
  input?: Partial<FamilyPlanConfig> | null,
  catalog: PlayerFamilyCatalog = PLAYER_FAMILY_CATALOG,
): FamilyPlanConfig {
  const plan = findPlayerPlan(inferPlanId(input, catalog), catalog);
  const sportIds = resolveSportIds(input, catalog);
  const category = resolveSubscriptionCategory(input);
  const includedChildren =
    category === 'family' ? catalog.familyHousehold.includedChildren : plan.includedChildren;
  const maxExtra = Math.max(0, catalog.maxChildren - includedChildren);
  const fromExtra = Number(input?.extraChildren);
  const fromTotal = Number(input?.childrenCount);
  const extraChildren =
    category === 'family'
      ? Math.min(
          maxExtra,
          Math.max(
            0,
            Number.isFinite(fromExtra)
              ? fromExtra
              : Number.isFinite(fromTotal)
                ? fromTotal - includedChildren
                : 0,
          ),
        )
      : 0;
  const childrenCount = category === 'family' ? includedChildren + extraChildren : includedChildren;
  const featureIds = uniqueStrings(
    Array.isArray(input?.featureAddOnIds) ? input.featureAddOnIds : [],
  ).filter((id) => catalog.featureAddOns.some((addOn) => addOn.id === id));
  const publicProfile = true;

  return {
    planId: plan.id,
    category,
    sportIds,
    sportsCount: sportIds.length,
    childrenCount,
    extraChildren,
    publicProfile,
    featureAddOnIds: featureIds,
    familyPackage: category === 'family',
    startDate: input?.startDate || null,
    prebookIntake: input?.prebookIntake || null,
  };
}

export interface FamilyPlanQuote {
  listUsd: number;
  payableUsd: number;
  discountPercent: number;
  earlyBirdActive: boolean;
  planPremiumUsd: number;
  includedSportsUsd: number;
  extraSports: number;
  extraSportsUsd: number;
  extraChildren: number;
  extraChildrenUsd: number;
  familyHouseholdUsd: number;
  publicProfileUsd: number;
  featureAddOnsUsd: number;
  baseListUsd: number;
  sportsLine: Array<{
    id: string;
    name: string;
    yearlyUsd: number;
    extra: boolean;
    beta?: boolean;
    prebook?: boolean;
  }>;
  featureLines: Array<{ id: string; name: string; yearlyUsd: number; included: boolean }>;
  individualToFamilyDiscountPercent: number;
  individualToFamilyDiscountUsd: number;
}

function applyEarlyBird(
  listUsd: number,
  offer?: SubscriptionOffer | null,
): Pick<FamilyPlanQuote, 'payableUsd' | 'discountPercent' | 'earlyBirdActive'> {
  const earlyBirdActive = Boolean(offer?.earlyBirdEnabled && (offer?.earlyBirdPercent || 0) > 0);
  const discountPercent = earlyBirdActive ? Number(offer?.earlyBirdPercent) || 0 : 0;
  return {
    earlyBirdActive,
    discountPercent,
    payableUsd: earlyBirdActive ? earlyBirdPrice(listUsd, discountPercent) : listUsd,
  };
}

export function quoteFamilyPlan(
  baseOffer: SubscriptionOffer | undefined,
  configInput?: Partial<FamilyPlanConfig> | null,
  catalog: PlayerFamilyCatalog = PLAYER_FAMILY_CATALOG,
  options?: { fromCategory?: SubscriptionCategory | null },
): FamilyPlanQuote | null {
  const config = clampFamilyPlanConfig(configInput, catalog);
  const plan = findPlayerPlan(config.planId, catalog);
  const sportsLine = config.sportIds.map((id, index) => {
    const sport = findCatalogSport(id, catalog);
    const extra = index >= plan.includedSports;
    return {
      id,
      name: sport?.name || id,
      yearlyUsd: extra ? catalog.extraSportUsd : 0,
      extra,
    };
  });
  const extraSports = sportsLine.filter((row) => row.extra).length;
  const extraSportsUsd = extraSports * catalog.extraSportUsd;
  const includedSportsUsd = 0;

  const extraChildren = config.category === 'family' ? config.extraChildren : 0;
  const extraChildrenUsd = extraChildren * catalog.extraChildUsd;
  const familyHouseholdUsd = 0;
  const planPrice = planYearlyAmount(plan, catalog, config.category);

  const featureLines: FamilyPlanQuote['featureLines'] = [];
  const featureAddOnsUsd = 0;
  const publicProfileUsd = 0;

  const listUsd = planPrice + extraSportsUsd + extraChildrenUsd;
  const early = applyEarlyBird(listUsd, baseOffer);
  const familyDiscount = applyIndividualToFamilyDiscount(
    early.payableUsd,
    catalog,
    options?.fromCategory,
    config.category,
  );

  return {
    listUsd,
    payableUsd: familyDiscount.payableUsd,
    discountPercent: early.discountPercent,
    earlyBirdActive: early.earlyBirdActive,
    individualToFamilyDiscountPercent: familyDiscount.individualToFamilyDiscountPercent,
    individualToFamilyDiscountUsd: familyDiscount.individualToFamilyDiscountUsd,
    planPremiumUsd: planPrice,
    includedSportsUsd,
    extraSports,
    extraSportsUsd,
    extraChildren,
    extraChildrenUsd,
    familyHouseholdUsd,
    publicProfileUsd,
    featureAddOnsUsd,
    baseListUsd: planPrice,
    sportsLine,
    featureLines,
  };
}

export function planFromPrice(
  plan: PlayerPlanOffering,
  catalog: PlayerFamilyCatalog = PLAYER_FAMILY_CATALOG,
  offer?: SubscriptionOffer,
  category: SubscriptionCategory = 'individual',
): { listUsd: number; payableUsd: number; earlyBirdActive: boolean; discountPercent: number } {
  const listUsd = planYearlyAmount(plan, catalog, category);
  return { listUsd, ...applyEarlyBird(listUsd, offer) };
}

export function earlyBirdUsuallyLine(discountPercent: number, listLabel: string): string {
  return `Early bird ${discountPercent}% off · usually ${listLabel} / year`;
}

export function familyPlanSummary(configInput?: Partial<FamilyPlanConfig> | null): string {
  const config = clampFamilyPlanConfig(configInput);
  const plan = findPlayerPlan(config.planId);
  const sportNames = config.sportIds
    .map((id) => findCatalogSport(id)?.name || id)
    .filter(Boolean);
  const seats = config.category === 'family' ? familyIncludedSeats() : '1 player';
  return [
    config.category === 'family' ? 'Family' : 'Individual',
    plan.name,
    seats,
    sportNames.length ? sportNames.join(', ') : 'sports chosen after payment',
  ].join(' · ');
}

export function isPlayerParentRole(role?: string | null): boolean {
  return String(role || '').toLowerCase() === 'player';
}

export function isCoachRole(role?: string | null): boolean {
  return String(role || '').toLowerCase() === 'coach';
}

/** 1.0: coaches buy the same Individual | Family player plans. */
export function showsPlayerFamilyPlans(role?: string | null): boolean {
  return isPlayerParentRole(role) || isCoachRole(role);
}

export function isOrgAdminRole(role?: string | null): boolean {
  return String(role || '').toLowerCase() === 'academy_admin';
}

/** Logged-in View plans / Purchase options stay in the dashboard. */
export function inAppPlansPath(
  role?: string | null,
  opts?: { academyType?: string | null },
): string {
  const r = String(role || '').toLowerCase();
  if (r === 'player') return '/player/profile?tab=subscription';
  if (r === 'coach') return '/coach?tab=subscription';
  if (r === 'academy_admin') return '/academy-admin/dashboard?tab=subscription';
  if (opts?.academyType) return '/academy-admin/dashboard?tab=subscription';
  return '/subscriptions';
}

export function inAppDashboardPath(role?: string | null): string {
  const r = String(role || '').toLowerCase();
  if (r === 'player') return '/player/dashboard';
  if (r === 'coach') return '/coach';
  if (r === 'academy_admin') return '/academy-admin/dashboard';
  return '/dashboard';
}

export function inAppInvoicesPath(role?: string | null): string {
  const r = String(role || '').toLowerCase();
  if (r === 'coach') return '/coach/invoices';
  if (r === 'academy_admin') return '/academy-admin/invoices';
  return '/player/invoices';
}

export function paymentGatewayPath(
  role?: string | null,
  gateway: 'stripe' | 'razorpay' = 'stripe',
): string {
  const r = String(role || '').toLowerCase();
  if (r === 'coach') return `/coach/billing/${gateway}`;
  if (r === 'academy_admin') return `/academy-admin/billing/${gateway}`;
  return `/player/billing/${gateway}`;
}

/** India / INR uses the EncycGames Razorpay page; everyone else uses Stripe. */
export function preferredPaymentGateway(
  currency?: string | null,
  countryCode?: string | null,
): 'stripe' | 'razorpay' {
  const cur = String(currency || '').toUpperCase();
  const cc = String(countryCode || '').toUpperCase();
  if (cur === 'INR' || cc === 'IN') return 'razorpay';
  return 'stripe';
}

/** EncycGames payment page for a yearly purchase (not the vendor hosted site). */
export function purchaseGatewayPath(opts: {
  role?: string | null;
  amount: number;
  plan: string;
  returnUrl?: string;
  currency?: string | null;
  countryCode?: string | null;
  gateway?: 'stripe' | 'razorpay';
}): string {
  const gateway = opts.gateway || preferredPaymentGateway(opts.currency, opts.countryCode);
  const base = paymentGatewayPath(opts.role, gateway);
  const params = new URLSearchParams();
  params.set('amount', String(opts.amount));
  params.set('plan', opts.plan);
  if (opts.returnUrl) params.set('returnUrl', opts.returnUrl);
  if (opts.currency) params.set('currency', opts.currency);
  return `${base}?${params.toString()}`;
}

export function inAppPurchasePath(role?: string | null): string {
  return inAppPlansPath(role);
}

export function publicPlansPath(): string {
  return '/subscriptions';
}

export function offersForAuthenticatedUser(
  offers: SubscriptionOffer[] = DEFAULT_SUBSCRIPTION_OFFERS,
  role?: string | null,
  academyType?: string | null,
): SubscriptionOffer[] {
  const r = String(role || '').toLowerCase();
  if (r === 'player') {
    return offers.filter((o) => o.id === 'players_parents');
  }
  if (r === 'coach') {
    return offers.filter((o) => o.id === 'players_parents');
  }
  if (r === 'academy_admin') {
    const suggested = suggestedOfferIdForRole(role, academyType);
    const match = offers.filter((o) => o.id === suggested);
    return match.length ? match : offers.filter((o) => o.id === 'academies');
  }
  return [];
}

export function audienceLabelForRole(role?: string | null, academyType?: string | null): string {
  const r = String(role || '').toLowerCase();
  if (r === 'player') return 'Players / Parents';
  if (r === 'coach') return 'Players / Parents';
  if (r === 'academy_admin') {
    const t = String(academyType || '').toLowerCase();
    if (t === 'regular_school' || t === 'college') return 'Schools/Colleges';
    if (t === 'corporate') return 'Corporate';
    return 'Academies';
  }
  return 'Yearly plan';
}

export function resolveOfferForUser(
  offers: SubscriptionOffer[],
  user?: Pick<User, 'role' | 'subscription'> | null,
  academyType?: string | null,
): SubscriptionOffer | undefined {
  const roleOffers = offersForAuthenticatedUser(offers, user?.role, academyType);
  const wanted = user?.subscription?.offerId || user?.subscription?.suggestedOfferId;
  return roleOffers.find((o) => o.id === wanted) || roleOffers[0];
}

function asNumber(value: unknown, fallback: number): number {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

/** Merge API catalog with local defaults so placeholder SKUs stay complete. */
export function normalizePlayerFamilyCatalog(raw?: Partial<PlayerFamilyCatalog> | null): PlayerFamilyCatalog {
  const base = PLAYER_FAMILY_CATALOG;
  const sportsRaw = Array.isArray(raw?.sports) ? raw.sports : base.sports;
  const sports: CatalogSport[] = sportsRaw.map((item) => {
    if (typeof item === 'string') {
      return findCatalogSport(item, base) || {
        id: normalizeSportId(item),
        name: item,
      };
    }
    const found = findCatalogSport(item.id || item.name, base);
    return {
      id: normalizeSportId(item.id || item.name) || found?.id || 'sport',
      name: item.name || found?.name || 'Sport',
    };
  });

  const plans: PlayerPlanOffering[] = PLAYER_PLAN_IDS.map((id) => {
    const fallback = findPlayerPlan(id, base);
    const incoming = Array.isArray(raw?.plans) ? raw.plans.find((plan) => plan.id === id) : undefined;
    const enabledByCategory = resolvePlanEnabledByCategory({
      id,
      enabled: incoming?.enabled ?? fallback.enabled,
      enabledByCategory: incoming?.enabledByCategory || fallback.enabledByCategory,
    });
    return {
      ...fallback,
      ...incoming,
      id,
      includedSports: asNumber(incoming?.includedSports, fallback.includedSports),
      includedChildren: asNumber(incoming?.includedChildren, fallback.includedChildren),
      premiumUsd: asNumber(incoming?.premiumUsd, fallback.premiumUsd),
      includesPublicProfile: true,
      enabledByCategory,
      enabled: enabledByCategory.individual || enabledByCategory.family,
      betaAccess: incoming?.betaAccess || fallback.betaAccess || 'none',
      beta: incoming?.beta ?? fallback.beta,
      prebook: incoming?.prebook ?? fallback.prebook,
      features:
        Array.isArray(incoming?.features) && incoming.features.length
          ? incoming.features.map(String)
          : fallback.features,
    };
  });

  const incomingAddOns = Array.isArray(raw?.featureAddOns) ? raw.featureAddOns : base.featureAddOns;
  const featureAddOns: FeatureAddOn[] = incomingAddOns
    .filter((addOn) => addOn && addOn.id !== 'public_profile' && addOn.id !== 'priority_support')
    .map((addOn) => {
      const fallback = base.featureAddOns.find((item) => item.id === addOn.id);
      return {
        id: String(addOn.id || fallback?.id || 'addon'),
        name: addOn.name || fallback?.name || 'Add-on',
        description: addOn.description || fallback?.description || '',
        yearlyUsd: asNumber(addOn.yearlyUsd, fallback?.yearlyUsd ?? 0),
        includedInPlans: Array.isArray(addOn.includedInPlans)
          ? addOn.includedInPlans.filter((id): id is PlayerPlanId => PLAYER_PLAN_IDS.includes(id))
          : fallback?.includedInPlans || [],
      };
    });

  const incomingRates = Array.isArray(raw?.countryRates) && raw.countryRates.length
    ? raw.countryRates
    : base.countryRates || DEFAULT_COUNTRY_RATES;
  const countryRates: SubscriptionCountryRate[] = incomingRates.map((row) => {
    const fallback =
      DEFAULT_COUNTRY_RATES.find((item) => item.code === String(row.code || '').toUpperCase()) ||
      defaultCountryRate();
    const asPlanPrices = (value: Partial<PlanPriceMap> | undefined, fallbackPrices: PlanPriceMap): PlanPriceMap => ({
      basic: asNumber(value?.basic, fallbackPrices.basic),
      multi: asNumber(value?.multi, fallbackPrices.multi),
      elite: asNumber(value?.elite, fallbackPrices.elite),
    });
    return {
      code: String(row.code || fallback.code).toUpperCase(),
      name: row.name || fallback.name,
      currency: String(row.currency || fallback.currency).toUpperCase(),
      individual: asPlanPrices(row.individual, fallback.individual),
      family: asPlanPrices(row.family, fallback.family),
      extraSport: asNumber(row.extraSport, fallback.extraSport),
      extraChild: asNumber(row.extraChild, fallback.extraChild),
    };
  });
  const defaultCountryCode = String(raw?.defaultCountryCode || base.defaultCountryCode || DEFAULT_SUBSCRIPTION_COUNTRY_CODE)
    .toUpperCase();
  const resolvedRate =
    countryRates.find((row) => row.code === String(raw?.countryCode || '').toUpperCase()) ||
    countryRates.find((row) => row.code === defaultCountryCode) ||
    countryRates[0];
  const planPrices = {
    individual: {
      ...(resolvedRate?.individual || base.planPrices?.individual || DEFAULT_COUNTRY_RATES[0].individual),
      ...(raw?.planPrices?.individual || {}),
    },
    family: {
      ...(resolvedRate?.family || base.planPrices?.family || DEFAULT_COUNTRY_RATES[0].family),
      ...(raw?.planPrices?.family || {}),
    },
  };

  return {
    ...base,
    ...raw,
    sports: sports.length ? sports : base.sports,
    plans,
    featureAddOns,
    familyHousehold: (() => {
      const incoming =
        (raw as { familyHousehold?: Partial<FamilyHousehold> } | null)?.familyHousehold || {};
      return {
        ...base.familyHousehold,
        ...incoming,
        includedAdults: asNumber(incoming.includedAdults, base.familyHousehold.includedAdults),
        includedChildren: asNumber(incoming.includedChildren, base.familyHousehold.includedChildren),
        yearlyUsd: asNumber(incoming.yearlyUsd, base.familyHousehold.yearlyUsd),
        extraChildUsd: asNumber(
          incoming.extraChildUsd,
          resolvedRate?.extraChild ?? base.familyHousehold.extraChildUsd,
        ),
        description: incoming.description || base.familyHousehold.description,
      };
    })(),
    extraSportUsd: asNumber(raw?.extraSportUsd, resolvedRate?.extraSport ?? base.extraSportUsd),
    extraChildUsd: asNumber(
      raw?.extraChildUsd,
      resolvedRate?.extraChild ??
        (raw as { familyHousehold?: Partial<FamilyHousehold> } | null)?.familyHousehold?.extraChildUsd ??
        base.extraChildUsd,
    ),
    currency: raw?.currency || resolvedRate?.currency || base.currency,
    countryCode: raw?.countryCode || resolvedRate?.code || base.countryCode,
    countryName: raw?.countryName || resolvedRate?.name || base.countryName,
    defaultCountryCode,
    planPrices,
    countryRates,
    individualToFamilyDiscountPercent: asNumber(
      raw?.individualToFamilyDiscountPercent,
      base.individualToFamilyDiscountPercent,
    ),
    publicProfileUsd: asNumber(raw?.publicProfileUsd, base.publicProfileUsd),
    maxSports: asNumber(raw?.maxSports, base.maxSports),
    maxChildren: asNumber(raw?.maxChildren, base.maxChildren),
    includedSports: asNumber(raw?.includedSports, base.includedSports),
    includedChildren: asNumber(raw?.includedChildren, base.includedChildren),
    headline: raw?.headline || base.headline,
    termsNote: raw?.termsNote || base.termsNote,
  };
}

export { getEffectiveYearlyPrice };
